import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useSEO } from "@/lib/seo";
import { getAttribution, getVisitorId, trackEvent } from "@/lib/analytics";
import { useLeadForm, leadField } from "@/lib/leadForms";
import ConsentCheckbox, { CONSENT_TEXT, validateConsent } from "@/components/ConsentCheckbox";
import PhoneInput from "@/components/PhoneInput";
import MediaImg from "@/components/MediaImg";
import { CalendarDays, Laptop, Layers, ShieldCheck, Users, Building2, Briefcase, Star, Clock, MapPin, MonitorSmartphone, Search, ArrowRight, User, Phone, Mail, GraduationCap, ClipboardList, CalendarClock, Bell, Circle, ChevronDown, Loader2, CheckCircle2 } from "lucide-react";

const YELLOW = "#FFC72C";
const ICONS = { CalendarDays, Laptop, Layers, ShieldCheck, Users, Building2, Briefcase, Star, ClipboardList, CalendarClock, Bell, Circle };
const Lic = ({ name, className }) => { const I = ICONS[name] || Circle; return <I className={className} />; };
const MODE_OPTIONS = ["Online (Live)", "Offline (Classroom)", "Hybrid (Online + Offline)"];
const SELECT_CLS = "w-full h-11 rounded-xl border border-slate-200 bg-white pl-10 pr-8 text-sm font-medium text-slate-800 appearance-none focus:outline-none focus:ring-2 focus:ring-[#FFC72C]";
const INPUT_CLS = "w-full h-11 rounded-xl border border-slate-200 bg-white pl-10 pr-4 text-sm font-medium text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#FFC72C]";

const fmtDate = (ts) => ts ? new Date(ts).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric", timeZone: "Asia/Kolkata" }) : "—";
const monthBucket = (ts) => ts ? new Date(ts).toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "Asia/Kolkata" }) : "";

export function RequestBatchForm({ id = "request-batch-form", programs, batches, preselect, compact = false, preview = false }) {
  const cfg = useLeadForm("batch_request", !preview);
  const [form, setForm] = useState({ name: "", phone: "", email: "", program_slug: "", batch_id: "", training_mode: "", schedule: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [consent, setConsent] = useState(true);

  useEffect(() => {
    if (preselect && (preselect.program_slug || preselect.batch_id)) {
      setForm((f) => ({ ...f, program_slug: preselect.program_slug || f.program_slug, batch_id: preselect.batch_id || f.batch_id }));
    }
  }, [preselect]);

  const fName = leadField(cfg, "name", { label: "Your Name", required: true });
  const fPhone = leadField(cfg, "phone", { label: "Mobile Number", required: true });
  const fEmail = leadField(cfg, "email", { label: "Email Address" });
  const fCourse = leadField(cfg, "program_slug", { label: "Select Course", required: true });
  const fBatch = leadField(cfg, "batch_id", { label: "Preferred Batch" });
  const fMode = leadField(cfg, "training_mode", { label: "Preferred Batch Mode", required: true });
  const fSched = leadField(cfg, "schedule", { label: "Preferred Schedule" });

  const courseBatches = useMemo(() => {
    if (!form.program_slug) return batches;
    return batches.filter((b) => b.program_slug === form.program_slug);
  }, [batches, form.program_slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (preview) return;
    if (!validateConsent(consent)) return;
    setLoading(true);
    try {
      const picked = batches.find((b) => b.id === form.batch_id);
      const { data } = await api.post("/inquiries", {
        name: form.name, phone: form.phone, email: form.email,
        program_slug: form.program_slug, batch_id: form.batch_id, batch_name: picked ? picked.name : "",
        training_mode: form.training_mode, preferred_schedule: form.schedule,
        source: "batch_request", attribution: getAttribution(), visitor_id: getVisitorId(),
        page: window.location.pathname,
        consent_given: consent, consent_text: CONSENT_TEXT,
      });
      trackEvent("inquiry_submit", { form: "batch_request", program_slug: form.program_slug, batch_id: form.batch_id });
      setDone(true);
      toast.success(data.message || "Request received");
    } catch (err) { toast.error(formatApiError(err)); } finally { setLoading(false); }
  };

  return (
    <div id={id} className="rounded-3xl bg-white shadow-2xl border border-slate-100 p-6 sm:p-7 w-full" data-testid="request-batch-form">
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-xl bg-[#FFC72C] text-black flex items-center justify-center shrink-0"><CalendarDays className="w-5 h-5" /></span>
        <div>
          <h3 className="text-lg font-extrabold text-slate-900 leading-tight">{cfg.title || "Request a Batch"}</h3>
          <p className="text-xs text-slate-500 mt-0.5">{cfg.subtitle || "Tell us your preferred course and schedule."}</p>
        </div>
      </div>
      {done ? (
        <div className="py-8 text-center" data-testid="request-batch-success">
          <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
          <p className="mt-3 font-extrabold text-slate-900">{cfg.success_title || "Request received!"}</p>
          <p className="text-xs text-slate-500 mt-1">{cfg.success_message || "We'll get in touch with batch details shortly."}</p>
        </div>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-2.5 mt-5">
          {fName.visible && (
            <div className="relative"><User className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input required={!!fName.required} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={`${fName.label}${fName.required ? " *" : ""}`} className={INPUT_CLS} data-testid="rb-name" /></div>
          )}
          {fPhone.visible && (
            <PhoneInput required={!!fPhone.required} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} placeholder={fPhone.label} inputCls={INPUT_CLS.replace("pl-10", "pl-4")} testid="rb-phone" />
          )}
          {fEmail.visible && (
            <div className="relative"><Mail className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input type="email" required={!!fEmail.required} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={fEmail.label} className={INPUT_CLS} data-testid="rb-email" /></div>
          )}
          {fCourse.visible && (
            <div className="relative"><GraduationCap className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <select required={!!fCourse.required} value={form.program_slug} onChange={(e) => setForm({ ...form, program_slug: e.target.value, batch_id: "" })} className={`${SELECT_CLS} ${form.program_slug ? "" : "text-slate-400"}`} data-testid="rb-course">
                <option value="">{fCourse.label}{fCourse.required ? " *" : ""}</option>
                {programs.map((p) => <option key={p.slug} value={p.slug}>{p.title}</option>)}
              </select><ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" /></div>
          )}
          {fBatch.visible && (
            <div className="relative"><CalendarDays className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <select value={form.batch_id} onChange={(e) => setForm({ ...form, batch_id: e.target.value })} className={`${SELECT_CLS} ${form.batch_id ? "" : "text-slate-400"}`} data-testid="rb-batch">
                <option value="">{fBatch.label}</option>
                {courseBatches.map((b) => <option key={b.id} value={b.id}>{b.program_name} — {fmtDate(b.start_date)}{b.branch ? ` · ${b.branch}` : ""}</option>)}
              </select><ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" /></div>
          )}
          {fMode.visible && (
            <div className="relative"><MonitorSmartphone className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <select required={!!fMode.required} value={form.training_mode} onChange={(e) => setForm({ ...form, training_mode: e.target.value })} className={`${SELECT_CLS} ${form.training_mode ? "" : "text-slate-400"}`} data-testid="rb-mode">
                <option value="">{fMode.label}{fMode.required ? " *" : ""}</option>
                {MODE_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select><ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" /></div>
          )}
          {fSched.visible && (
            <div className="relative"><Clock className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input value={form.schedule} onChange={(e) => setForm({ ...form, schedule: e.target.value })} placeholder={fSched.label} className={INPUT_CLS} data-testid="rb-schedule" /></div>
          )}
          <ConsentCheckbox checked={consent} onChange={setConsent} testid="rb-consent" className="mt-1" />
          <button type="submit" disabled={loading}
            className="w-full h-12 rounded-xl bg-[#FFC72C] hover:bg-[#F5B800] disabled:opacity-60 text-black font-extrabold text-sm flex items-center justify-center gap-2 transition-colors mt-1"
            data-testid="rb-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (cfg.button_label || "Request Batch")}
          </button>
          <p className="flex items-center justify-center gap-1.5 text-[11px] text-slate-500 mt-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> We'll get in touch with batch details shortly.</p>
        </form>
      )}
    </div>
  );
}

export function UBHero({ section, formNode, interactive = true }) {
  const s = section || {};
  return (
    <section className="relative overflow-hidden" style={{ background: "linear-gradient(115deg, #FFF7DD 0%, #FFECB0 45%, #FFC72C 100%)" }} data-testid="ub-hero">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 pb-12 grid lg:grid-cols-[1.2fr_auto] gap-10 items-center">
        <div>
          <span className="inline-flex items-center rounded-full bg-black text-white text-[11px] font-extrabold px-4 py-1.5" data-testid="ub-badge">{s.badge || "Upcoming Batches"}</span>
          <h1 className="mt-4 text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.08]">
            {s.title_black || "Upcoming"} <span className="bg-black text-[#FFC72C] px-3 rounded-md inline-block">{s.title_accent || "Live Batches"}</span>
          </h1>
          <p className="mt-3 text-lg sm:text-xl font-bold text-slate-800">{s.subtitle || "Join the Next Batch & Become Job Ready"}</p>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mt-6">
            {(s.benefits || []).filter((b) => b.visible !== false).map((b, i) => (
              <span key={i} className="flex items-center gap-2 text-xs font-extrabold text-slate-900" data-testid={`ub-benefit-${i}`}>
                <span className="w-9 h-9 rounded-lg bg-black/5 border border-black/10 flex items-center justify-center"><Lic name={b.icon} className="w-4.5 h-4.5 w-5 h-5 text-slate-900" /></span>
                <span className="max-w-[110px] leading-tight">{b.label}</span>
              </span>
            ))}
          </div>
          {s.image && (
            <div className="mt-8 hidden lg:block">
              <MediaImg src={s.image} alt="Student learning" className="rounded-2xl max-w-md shadow-lg" />
            </div>
          )}
        </div>
        <div className="w-full max-w-md justify-self-center lg:justify-self-end">{formNode}</div>
      </div>
    </section>
  );
}

export function UBStats({ section }) {
  const items = (section?.items || []).filter((i) => i.visible !== false);
  if (!items.length) return null;
  return (
    <section className="bg-white border-b border-slate-100" data-testid="ub-stats">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-3.5" data-testid={`ub-stat-${i}`}>
            <Lic name={it.icon} className="w-8 h-8 text-[#FFC72C] shrink-0" />
            <div><p className="text-2xl font-extrabold text-slate-900 leading-tight">{it.value}</p><p className="text-xs text-slate-500 font-semibold">{it.label}</p></div>
          </div>
        ))}
      </div>
    </section>
  );
}

function BatchCard({ b, onRequest }) {
  const seats = typeof b.seats_left === "number";
  const low = seats && b.seats_left <= 6;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 hover:shadow-lg hover:border-[#FFC72C]/60 transition-all flex flex-col" data-testid={`batch-card-${b.id}`}>
      <div className="flex items-start justify-between gap-3">
        {b.program_image ? (
          <MediaImg src={b.program_image} alt={b.program_name} className="h-10 w-10 object-contain" />
        ) : (
          <span className="w-10 h-10 rounded-xl bg-slate-900 text-[#FFC72C] flex items-center justify-center text-xs font-extrabold shrink-0">{(b.program_name || "TB").split(" ").map((w) => w[0]).slice(0, 2).join("")}</span>
        )}
        {b.category && <span className="rounded-full bg-[#FFC72C]/15 text-[#9A7B0A] text-[10px] font-extrabold px-3 py-1" data-testid={`batch-cat-${b.id}`}>{b.category}</span>}
      </div>
      <h3 className="mt-3 font-extrabold text-slate-900 leading-snug">{b.program_name || b.name}</h3>
      <p className="text-[11px] text-slate-400 font-mono mt-0.5">{b.batch_number ? `#${b.batch_number}` : ""} {b.name}</p>
      <div className="flex flex-col gap-1.5 mt-3 text-[13px] text-slate-600">
        <span className="flex items-center gap-2"><CalendarDays className="w-4 h-4 text-slate-400" />{fmtDate(b.start_date)}</span>
        {b.timing && <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-slate-400" />{b.timing}</span>}
        <span className="flex items-center gap-2">
          {b.training_mode && b.training_mode.toLowerCase().includes("online") ? <MonitorSmartphone className="w-4 h-4 text-slate-400" /> : <MapPin className="w-4 h-4 text-slate-400" />}
          {b.training_mode || "—"}{b.branch ? ` (${b.branch})` : ""}
        </span>
        {seats && (
          <span className={`inline-flex items-center gap-1.5 text-[11px] font-extrabold px-2.5 py-1 rounded-full w-fit ${low ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-700"}`} data-testid={`batch-seats-${b.id}`}>
            <Users className="w-3.5 h-3.5" /> {b.seats_left} Seats Left
          </span>
        )}
      </div>
      <div className="flex items-center gap-2.5 mt-4 pt-4 border-t border-slate-100">
        {b.program_slug ? (
          <Link to={`/courses/${b.program_slug}`} className="flex-1 text-center text-xs font-bold px-3 py-2.5 rounded-xl border border-slate-200 text-slate-700 hover:border-slate-400 transition-colors" data-testid={`batch-view-${b.id}`}>View Details</Link>
        ) : (
          <span className="flex-1 text-center text-xs font-bold px-3 py-2.5 rounded-xl border border-slate-100 text-slate-300 cursor-not-allowed">View Details</span>
        )}
        <button onClick={() => onRequest(b)} className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-extrabold px-3 py-2.5 rounded-xl bg-[#FFC72C] hover:bg-[#F5B800] text-black transition-colors" data-testid={`batch-request-${b.id}`}>
          Request Batch <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

export function UBExplore({ section, batches, onRequest }) {
  const [cat, setCat] = useState("all");
  const [loc, setLoc] = useState("all");
  const [mode, setMode] = useState("all");
  const [month, setMonth] = useState("all");
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);

  const cats = useMemo(() => [...new Set(batches.map((b) => b.category).filter(Boolean))], [batches]);
  const locs = useMemo(() => [...new Set(batches.map((b) => b.branch).filter(Boolean))], [batches]);
  const modes = useMemo(() => [...new Set(batches.map((b) => b.training_mode).filter(Boolean))], [batches]);
  const months = useMemo(() => [...new Set(batches.map((b) => monthBucket(b.start_date)).filter(Boolean))], [batches]);

  const filtered = useMemo(() => batches.filter((b) => {
    if (cat !== "all" && b.category !== cat) return false;
    if (loc !== "all" && b.branch !== loc) return false;
    if (mode !== "all" && b.training_mode !== mode) return false;
    if (month !== "all" && monthBucket(b.start_date) !== month) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      if (!`${b.program_name || ""} ${b.name || ""} ${b.batch_number || ""}`.toLowerCase().includes(q)) return false;
    }
    return true;
  }), [batches, cat, loc, mode, month, search]);

  const visible = showAll ? filtered : filtered.slice(0, 6);

  return (
    <section className="bg-white" data-testid="ub-explore">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
        <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 text-center">{section?.title || "Explore Our Upcoming Batches"}</h2>
        <p className="text-sm text-slate-500 text-center mt-2">{section?.subtitle || "Choose a course, check the next batch date and secure your seat today."}</p>

        <div className="flex flex-wrap items-center justify-center gap-2.5 mt-8" data-testid="ub-cat-pills">
          {["all", ...cats].map((c) => (
            <button key={c} onClick={() => { setCat(c); setShowAll(false); }}
              className={`px-5 py-2.5 rounded-full text-xs font-extrabold transition-colors ${cat === c ? "bg-[#FFC72C] text-black" : "bg-white border border-slate-200 text-slate-600 hover:border-[#FFC72C]"}`}
              data-testid={`ub-cat-${c === "all" ? "all" : c.replace(/\s+/g, "-").toLowerCase()}`}>
              {c === "all" ? "All Courses" : c}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
          <div className="relative"><MapPin className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <select value={loc} onChange={(e) => setLoc(e.target.value)} className={SELECT_CLS} data-testid="ub-filter-location">
              <option value="all">All Locations</option>
              {locs.map((l) => <option key={l} value={l}>{l}</option>)}
            </select><ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" /></div>
          <div className="relative"><MonitorSmartphone className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <select value={mode} onChange={(e) => setMode(e.target.value)} className={SELECT_CLS} data-testid="ub-filter-mode">
              <option value="all">All Modes</option>
              {modes.map((m) => <option key={m} value={m}>{m}</option>)}
            </select><ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" /></div>
          <div className="relative"><CalendarDays className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <select value={month} onChange={(e) => setMonth(e.target.value)} className={SELECT_CLS} data-testid="ub-filter-month">
              <option value="all">All Upcoming Dates</option>
              {months.map((m) => <option key={m} value={m}>{m}</option>)}
            </select><ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" /></div>
          <div className="relative"><Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search course or batch..." className={`${INPUT_CLS} pr-4`} data-testid="ub-search" /></div>
        </div>

        {visible.length === 0 ? (
          <div className="text-center py-16" data-testid="ub-empty">
            <p className="font-extrabold text-slate-800">No upcoming batches match these filters</p>
            <p className="text-sm text-slate-500 mt-1.5">Request a batch below and we'll schedule one for you.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8" data-testid="ub-batch-grid">
            {visible.map((b) => <BatchCard key={b.id} b={b} onRequest={onRequest} />)}
          </div>
        )}

        {filtered.length > 6 && !showAll && (
          <div className="text-center mt-10">
            <button onClick={() => setShowAll(true)} className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border-2 border-slate-900 text-sm font-extrabold text-slate-900 hover:bg-slate-900 hover:text-white transition-colors" data-testid="ub-view-all">
              View All Upcoming Batches <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

export function UBCta({ section, onRequest }) {
  const s = section || {};
  const steps = (s.steps || []).filter((x) => x.visible !== false);
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16 w-full" data-testid="ub-cta">
      <div className="rounded-3xl p-8 sm:p-10 flex flex-col lg:flex-row lg:items-center gap-8" style={{ background: "linear-gradient(100deg, #FFF7DD 0%, #FFECB0 100%)" }}>
        <div className="flex-1">
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{s.title || "Not Finding a Suitable Batch?"}</h2>
          <p className="text-sm text-slate-600 mt-2 max-w-md">{s.subtitle || "Let us know your preferred course, timing and mode. We'll notify you as soon as a new batch is scheduled."}</p>
        </div>
        <div className="flex flex-wrap items-center gap-6 flex-1">
          {steps.map((st, i) => (
            <div key={i} className="flex items-center gap-2.5" data-testid={`ub-cta-step-${i}`}>
              <span className="w-9 h-9 rounded-lg bg-white/70 border border-black/5 flex items-center justify-center"><Lic name={st.icon} className="w-4.5 h-4.5 w-5 h-5 text-slate-900" /></span>
              <span className="text-xs font-extrabold text-slate-900 max-w-[110px] leading-tight">{st.title}</span>
            </div>
          ))}
        </div>
        <button onClick={() => onRequest(null)} className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-black text-white text-sm font-extrabold hover:bg-slate-800 transition-colors shrink-0" data-testid="ub-cta-request">
          {s.button_label || "Request a Batch"} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </section>
  );
}

export default function UpcomingBatches() {
  const [sections, setSections] = useState(null);
  const [batches, setBatches] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [preselect, setPreselect] = useState(null);

  useSEO({
    title: "Upcoming Batches — Join the Next Live IT Training Batch | Training Basket",
    description: "Explore upcoming instructor-led batches across cloud, data, networking, marketing and software courses. Check dates, modes and seats, and request a batch that fits your schedule.",
    canonical: `${window.location.origin}/upcoming-batches`,
  });

  useEffect(() => {
    trackEvent("page_view", { page: "upcoming_batches" });
    api.get("/website/sections").then((r) => {
      const m = {};
      (r.data.sections || []).forEach((s) => { m[s.key] = s; });
      setSections(m);
    }).catch(() => setSections({}));
    api.get("/batches/upcoming").then((r) => setBatches(r.data.batches || [])).catch(() => { setBatches([]); toast.error("Could not load upcoming batches right now"); });
    api.get("/programs").then((r) => setPrograms(r.data.programs || [])).catch(() => {});
  }, []);

  const onRequest = (b) => {
    if (b) setPreselect({ program_slug: b.program_slug || "", batch_id: b.id });
    trackEvent("form_view", { form: "batch_request", batch_id: b?.id || "" });
    document.getElementById("request-batch-form")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  if (!sections) return <div className="min-h-[70vh] bg-white" data-testid="ub-loading" />;

  const s = (k) => sections[k];
  return (
    <div className="flex flex-col bg-white" data-testid="upcoming-batches-page">
      <UBHero section={s("upcoming_hero")} formNode={<RequestBatchForm programs={programs} batches={batches} preselect={preselect} />} />
      <UBStats section={s("upcoming_stats")} />
      <UBExplore section={s("upcoming_explore")} batches={batches} onRequest={onRequest} />
      <UBCta section={s("upcoming_cta")} onRequest={onRequest} />
    </div>
  );
}
